import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { LOOTBOX_RARITIES } from '../../constants/lootbox';
import { VfxSpan } from '../../utils/helpers';
import ItemIcon from '../lootbox/ItemIcon';
import { VFX_EFFECTS } from '../../constants/vfx';
import { useToast } from '../ToastContext';

function BrowseListings({ listings, userData, onBuyNow, onPlaceBid, isS2 }) {
  const toast = useToast();
  const [bidAmounts, setBidAmounts] = useState({});
  const [selectedItemForInfo, setSelectedItemForInfo] = useState(null);
  const [salesHistory, setSalesHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Sorting and filtering state
  const [sortBy, setSortBy] = useState('rarity'); // 'rarity', 'price-low', 'price-high', 'name'
  const [filterStatTrack, setFilterStatTrack] = useState('all'); // 'all', 'stattrack', 'non-stattrack'
  const [filterShiny, setFilterShiny] = useState('all'); // 'all', 'shiny', 'non-shiny'
  const [filterRarity, setFilterRarity] = useState('all'); // 'all' or specific rarity
  const [filterOwnership, setFilterOwnership] = useState('all'); // 'all', 'not-owned', 'owned'
  const [filterVfx, setFilterVfx] = useState('all'); // 'all', 'vfx', 'non-vfx'

  // Format stat names: remove underscores and capitalize properly
  function formatStatName(stat) {
    return stat
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Update current time every second for countdown timers
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  function formatTimeRemaining(endTime) {
    const remaining = endTime - currentTime;
    if (remaining <= 0) return "Ended";

    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  async function handleShowItemInfo(item) {
    setSelectedItemForInfo(item);
    setLoadingHistory(true);

    try {
      // Load sales history for this item
      const historyQuery = query(
        collection(db, "marketplaceHistory"),
        orderBy("timestamp", "desc")
      );
      const historySnapshot = await getDocs(historyQuery);

      // Show sales history for this specific emoji variant (matching rarity and shiny status)
      const itemHistory = historySnapshot.docs
        .map(doc => doc.data())
        .filter(sale =>
          sale.itemName === item.name &&
          sale.itemRarity === item.rarity &&
          (sale.isShiny || false) === (item.isShiny || false)
        )
        .slice(0, 20); // Show last 20 sales

      setSalesHistory(itemHistory);
    } catch (error) {
      console.error("Error loading sales history:", error);
      setSalesHistory([]);
    }

    setLoadingHistory(false);
  }

  // Apply filtering and sorting
  const filteredAndSortedListings = React.useMemo(() => {
    let result = [...listings];

    // Apply filters
    if (filterStatTrack === 'stattrack') {
      result = result.filter(l => l.item.isStatTrak);
    } else if (filterStatTrack === 'non-stattrack') {
      result = result.filter(l => !l.item.isStatTrak);
    }

    if (filterShiny === 'shiny') {
      result = result.filter(l => l.item.isShiny);
    } else if (filterShiny === 'non-shiny') {
      result = result.filter(l => !l.item.isShiny);
    }

    if (filterRarity !== 'all') {
      result = result.filter(l => l.item.rarity === filterRarity);
    }

    if (filterVfx === 'vfx') {
      result = result.filter(l => l.item.vfx && l.item.vfx.length > 0);
    } else if (filterVfx === 'non-vfx') {
      result = result.filter(l => !l.item.vfx || l.item.vfx.length === 0);
    }

    // Apply ownership filter
    if (filterOwnership !== 'all' && userData && userData.lootboxInventory) {
      const userInventory = userData.lootboxInventory || [];

      if (filterOwnership === 'not-owned') {
        // Show only items not in user's inventory
        result = result.filter(listing => {
          return !userInventory.some(invItem =>
            invItem.id === listing.item.id &&
            invItem.rarity === listing.item.rarity &&
            (invItem.isShiny || false) === (listing.item.isShiny || false)
          );
        });
      } else if (filterOwnership === 'owned') {
        // Show only items user already owns
        result = result.filter(listing => {
          return userInventory.some(invItem =>
            invItem.id === listing.item.id &&
            invItem.rarity === listing.item.rarity &&
            (invItem.isShiny || false) === (listing.item.isShiny || false)
          );
        });
      }
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortBy === 'rarity') {
        // Sort by rarity (high to low), then alphabetically by name
        const rarityOrder = { 'NUCLEAR': 8, 'GALAXY': 7, 'MYTHIC': 6, 'LEGENDARY': 5, 'EPIC': 4, 'RARE': 3, 'UNCOMMON': 2, 'COMMON': 1 };
        const rarityDiff = rarityOrder[b.item.rarity] - rarityOrder[a.item.rarity];
        if (rarityDiff !== 0) return rarityDiff;
        return a.item.name.localeCompare(b.item.name);
      } else if (sortBy === 'price-low') {
        return a.price - b.price;
      } else if (sortBy === 'price-high') {
        return b.price - a.price;
      } else if (sortBy === 'name') {
        return a.item.name.localeCompare(b.item.name);
      }
      return 0;
    });

    return result;
  }, [listings, sortBy, filterStatTrack, filterShiny, filterRarity, filterOwnership, filterVfx, userData]);

  return (
    <>
      {/* Sorting and Filtering Controls */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '20px',
        padding: '16px',
        background: 'var(--bg-card)',
        borderRadius: '12px',
        border: '2px solid var(--border)'
      }}>
        {/* Sort By */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)' }}>Sort By</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="rarity">Rarity (High to Low)</option>
            <option value="price-low">Price (Low to High)</option>
            <option value="price-high">Price (High to Low)</option>
            <option value="name">Name (A-Z)</option>
          </select>
        </div>

        {/* StatTrack Filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)' }}>StatTrak</label>
          <select
            value={filterStatTrack}
            onChange={(e) => setFilterStatTrack(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Items</option>
            <option value="stattrack">StatTrak Only</option>
            <option value="non-stattrack">Non-StatTrak</option>
          </select>
        </div>

        {/* Shiny Filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)' }}>Shiny</label>
          <select
            value={filterShiny}
            onChange={(e) => setFilterShiny(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Items</option>
            <option value="shiny">Shiny Only</option>
            <option value="non-shiny">Non-Shiny</option>
          </select>
        </div>

        {/* VFX Filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)' }}>VFX</label>
          <select
            value={filterVfx}
            onChange={(e) => setFilterVfx(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Items</option>
            <option value="vfx">VFX Only</option>
            <option value="non-vfx">No VFX</option>
          </select>
        </div>

        {/* Rarity Filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)' }}>Rarity</label>
          <select
            value={filterRarity}
            onChange={(e) => setFilterRarity(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Rarities</option>
            {!isS2 && <option value="NUCLEAR">Nuclear</option>}
            <option value="GALAXY">Galaxy</option>
            <option value="MYTHIC">Mythic</option>
            <option value="LEGENDARY">Legendary</option>
            <option value="EPIC">Epic</option>
            <option value="RARE">Rare</option>
            <option value="UNCOMMON">Uncommon</option>
            <option value="COMMON">Common</option>
          </select>
        </div>

        {/* Ownership Filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)' }}>Ownership</label>
          <select
            value={filterOwnership}
            onChange={(e) => setFilterOwnership(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Items</option>
            <option value="not-owned">Not Owned</option>
            <option value="owned">Already Own</option>
          </select>
        </div>

        {/* Results count */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          marginLeft: 'auto',
          fontSize: '14px',
          color: 'var(--text-secondary)',
          fontWeight: '600'
        }}>
          {filteredAndSortedListings.length} item{filteredAndSortedListings.length !== 1 ? 's' : ''}
        </div>
      </div>

      {filteredAndSortedListings.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <h2>No Items Match Your Filters</h2>
          <p>Try adjusting your filters to see more listings</p>
        </div>
      ) : (
      <div className="marketplace-listings">
      {filteredAndSortedListings.map(listing => {
        const rarity = LOOTBOX_RARITIES[listing.item.rarity];
        // Minimum bid is either current bid + 1, or the starting price
        const minBid = listing.currentBid > 0 ? parseFloat((listing.currentBid + 0.01).toFixed(2)) : listing.price;
        const tokenIcon = listing.currencyType === "crewmate" ? "🔵" : "🔴";
        // Currency is locked to listing's currency type
        const currentBidCurrency = listing.currencyType;
        const currentBidIcon = currentBidCurrency === "crewmate" ? "🔵" : "🔴";
        const tokenField = listing.currencyType === "crewmate" ? "crewmateTokens" : "imposterTokens";

        const isShinyGalaxy = listing.item.isShiny && listing.item.rarity === 'GALAXY';
        const isNuclear = listing.item.rarity === 'NUCLEAR';

        return (
          <div
            key={listing.id}
            className={`marketplace-item ${rarity.glow ? 'glow' : ''} ${isNuclear ? 'nuclear' : isShinyGalaxy ? 'shiny-galaxy' : listing.item.isShiny ? 'shiny' : ''}`}
            style={{
              borderColor: isNuclear || isShinyGalaxy ? 'transparent' : rarity.color,
              boxShadow: isNuclear || isShinyGalaxy
                ? '' // Handled by nuclear or shiny-galaxy animation
                : (rarity.glow ? `0 0 15px ${rarity.color}` : 'none')
            }}
          >
            {isNuclear ? (
              <div className="nuclear-badge">☢️ NUCLEAR ☢️</div>
            ) : isShinyGalaxy ? (
              <div className="shiny-galaxy-badge" style={{ top: '4px', left: '4px', transform: 'none', fontSize: '8px', padding: '2px 6px' }}>🌌 ✨</div>
            ) : listing.item.isShiny && (
              <div className="shiny-badge-small">✨</div>
            )}
            {listing.item.isStatTrak && (
              <div className="stattrak-badge-small" style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                fontSize: '14px',
                background: 'rgba(255, 140, 0, 0.9)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontWeight: 'bold',
                color: '#000'
              }}>
                ST
              </div>
            )}
            <div className="marketplace-item-icon">
              {listing.item.vfx && listing.item.vfx.length > 0
                ? <VfxSpan vfx={listing.item.vfx} style={{ display: 'inline-block', fontSize: 'inherit' }}><ItemIcon item={listing.item} size={48} /></VfxSpan>
                : <ItemIcon item={listing.item} size={48} />}
            </div>
            <div className={`marketplace-item-name ${rarity.rainbow && !listing.item.isShiny ? 'rainbow-glow' : ''}`} style={{ color: listing.item.isShiny ? '#FFD700' : rarity.color }}>
              {listing.item.vfx && listing.item.vfx.length > 0
                ? <VfxSpan vfx={listing.item.vfx}>{listing.item.name}</VfxSpan>
                : listing.item.name}
            </div>
            {listing.item.vfx && listing.item.vfx.length > 0 && (
              <div style={{ fontSize: '11px', color: '#a78bfa', fontWeight: 'bold', marginBottom: '4px' }}>
                ✦ VFX: {(Array.isArray(listing.item.vfx) ? listing.item.vfx : [listing.item.vfx]).map(id => {
                  const e = VFX_EFFECTS.find(e => e.id === id);
                  return e ? e.name : id;
                }).join(', ')}
              </div>
            )}
            <div className={`marketplace-item-rarity ${rarity.rainbow ? 'rainbow-glow' : ''}`} style={{ color: rarity.color }}>
              {rarity.name}
            </div>

            {listing.item.isStatTrak && listing.item.statValues && (
              <div style={{
                marginTop: '8px',
                padding: '8px',
                background: 'rgba(255, 140, 0, 0.1)',
                border: '1px solid rgba(255, 140, 0, 0.3)',
                borderRadius: '6px',
                fontSize: '11px'
              }}>
                <div style={{ fontWeight: 'bold', color: '#FF8C00', marginBottom: '4px' }}>StatTrak™</div>
                {Object.entries(listing.item.statValues).map(([stat, value]) => (
                  <div key={stat} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{formatStatName(stat)}:</span>
                    <span style={{ color: '#FF8C00', fontWeight: 'bold' }}>{value}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="marketplace-item-seller">
              Sold by: {listing.sellerEmail.split('@')[0]}
            </div>

            {listing.auctionMode ? (
              <div className="marketplace-auction">
                <div className="auction-info">
                  <div className="auction-current-bid">
                    Current Bid: {listing.currentBid || 0} {listing.currentBid > 0 ? (listing.currentBidCurrency === "crewmate" ? "🔵" : "🔴") : tokenIcon}
                  </div>
                  <div className="auction-min-bid">
                    Starting Bid: {listing.price} {tokenIcon}
                  </div>
                  <div className="auction-min-bid" style={{ fontSize: '12px', color: 'var(--text-dim)' }}>
                    Minimum Bid: {minBid} {tokenIcon}
                  </div>
                  {listing.auctionEndTime && (
                    <div className="auction-timer">
                      ⏰ {formatTimeRemaining(listing.auctionEndTime)}
                    </div>
                  )}
                </div>
                <div className="bid-currency-selector">
                  <div style={{
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontSize: '14px',
                    border: '1px solid var(--border)'
                  }}>
                    Currency: {currentBidCurrency === "crewmate" ? "🔵 Crewmate" : "🔴 Imposter"}
                  </div>
                </div>
                <input
                  type="number"
                  className="bid-input"
                  placeholder={`Min: ${minBid}`}
                  min={minBid}
                  step="0.01"
                  value={bidAmounts[listing.id] || ''}
                  onChange={(e) => setBidAmounts({
                    ...bidAmounts,
                    [listing.id]: e.target.value === '' ? '' : (parseFloat(e.target.value) || '')
                  })}
                />
                <button
                  className="btn-bid"
                  onClick={() => {
                    const amount = bidAmounts[listing.id];
                    if (amount >= minBid) {
                      onPlaceBid(listing, amount, currentBidCurrency);
                    } else {
                      toast.warn(`Bid must be at least ${minBid} tokens!`);
                    }
                  }}
                >
                  Place Bid {currentBidIcon}
                </button>
              </div>
            ) : (
              <div className="marketplace-buy">
                <div className="marketplace-price">{listing.price} {tokenIcon}</div>
                <button
                  className="btn-buy"
                  onClick={() => onBuyNow(listing)}
                  disabled={(userData[tokenField] || 0) < listing.price}
                >
                  Buy Now
                </button>
              </div>
            )}

            <button
              className="btn-item-info"
              onClick={() => handleShowItemInfo(listing.item)}
              title="View item details and sales history"
            >
              ℹ️ Info
            </button>
          </div>
        );
      })}

      {/* Item Detail Modal */}
      {selectedItemForInfo && (
        <div className="modal-overlay" onClick={() => setSelectedItemForInfo(null)}>
          <div className="modal-content item-detail-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedItemForInfo(null)}>×</button>

            <div className="item-detail-header">
              <div className="item-detail-icon"><ItemIcon item={selectedItemForInfo} size={64} /></div>
              <div className="item-detail-title">
                <h2>{selectedItemForInfo.name}</h2>
                <div className="item-detail-rarity" style={{ color: LOOTBOX_RARITIES[selectedItemForInfo.rarity].color }}>
                  {LOOTBOX_RARITIES[selectedItemForInfo.rarity].name}
                  {selectedItemForInfo.isShiny && " ✨ SHINY"}
                </div>
              </div>
            </div>

            <div className="item-detail-section">
              <h3>Sales History</h3>
              {loadingHistory ? (
                <div className="loading-text">Loading sales data...</div>
              ) : salesHistory.length > 0 ? (
                <div className="sales-history-list">
                  {salesHistory.map((sale, index) => {
                    const saleDate = new Date(sale.timestamp);
                    const tokenIcon = sale.currencyType === "crewmate" ? "🔵" : "🔴";
                    const saleRarity = LOOTBOX_RARITIES[sale.itemRarity];
                    return (
                      <div key={index} className="sales-history-item">
                        <div className="sale-info">
                          <div className="sale-variant" style={{ color: saleRarity?.color || '#fff' }}>
                            {saleRarity?.name || 'Unknown'}{sale.isShiny ? ' ✨' : ''}
                          </div>
                          <div className="sale-date">
                            {saleDate.toLocaleDateString()} {saleDate.toLocaleTimeString()}
                          </div>
                        </div>
                        <div className="sale-price">
                          {sale.price} {tokenIcon}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state-small">
                  <p>No sales history available for this item</p>
                </div>
              )}
            </div>

            {salesHistory.length > 0 && (
              <div className="item-detail-stats">
                <div className="stat-box">
                  <div className="stat-label">Total Sales</div>
                  <div className="stat-value">{salesHistory.length}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Average Price</div>
                  <div className="stat-value">
                    {parseFloat((salesHistory.reduce((sum, s) => sum + s.price, 0) / salesHistory.length).toFixed(2))}
                  </div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Lowest Price</div>
                  <div className="stat-value">{Math.min(...salesHistory.map(s => s.price))}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Highest Price</div>
                  <div className="stat-value">{Math.max(...salesHistory.map(s => s.price))}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
      )}
    </>
  );
}

export default BrowseListings;
